import * as React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, GripVertical, X } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { DashboardLayoutItem } from "../../types";
import { canUseWidget, WIDGET_REGISTRY, WidgetProps, WidgetType } from "./widgetRegistry";
import LockedWidget from "./LockedWidget";

interface IProps {
  item: DashboardLayoutItem;
  editing: boolean;
  onRemove?(): void;
  // The grid drags widgets by their grip; the mobile list reorders them with up/down buttons instead
  reorder?: "drag" | "buttons";
  onMoveUp?(): void;
  onMoveDown?(): void;
  widgetProps: WidgetProps;
}

const DashboardWidget = ({ item, editing, onRemove, reorder = "drag", onMoveUp, onMoveDown, widgetProps }: IProps) => {
  const { t } = useTranslation();
  const definition = WIDGET_REGISTRY[item.type as WidgetType];
  if (!definition) return null;

  const WidgetComponent = definition.component;
  const body = canUseWidget(item.type)
    ? <WidgetComponent {...widgetProps} />
    : <LockedWidget title={t(definition.title)} />;

  // Keep the same wrapper/body elements in both modes so the widget doesn't remount (refetch,
  // lose internal state) when toggling edit mode; only classes and the toolbar sibling change.
  return (
    <div
      className={cn(
        "relative h-full",
        editing && "rounded-lg outline-2 outline-dashed outline-offset-4 outline-border"
      )}
    >
      <div className={cn("h-full", editing && "pointer-events-none select-none")}>{body}</div>
      {editing && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 rounded-md border bg-background p-0.5 shadow-sm">
          {reorder === "drag" ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="dashboard-widget-drag-handle cursor-grab active:cursor-grabbing"
              aria-label={t("Dashboard::Move widget")}
            >
              <GripVertical />
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onMoveUp}
                disabled={!onMoveUp}
                aria-label={t("Dashboard::Move widget up")}
              >
                <ArrowUp />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onMoveDown}
                disabled={!onMoveDown}
                aria-label={t("Dashboard::Move widget down")}
              >
                <ArrowDown />
              </Button>
            </>
          )}
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onRemove}
              aria-label={t("Dashboard::Remove widget")}
            >
              <X />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardWidget;
